import axios from "axios";
import config from "./config/config";
import { PUSH_DEVICE_ID_KEY, unsubscribeThisDevice } from "./api/push";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

// Fonction pour décoder le JWT et obtenir les informations du payload
function parseJwt(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

function isJwtExpired(token) {
  if (!token) return true;
  try {
    const currentTime = Math.floor(Date.now() / 1000); // Timestamp en secondes
    return parseJwt(token).exp < currentTime;
  } catch {
    return true; // Token illisible : considéré comme expiré
  }
}

// Vérifier si la session est expirée.
// Un access token expiré ne suffit pas : tant que le refresh token est valide,
// la prochaine requête renouvellera la session (voir l'intercepteur ci-dessous).
export function isTokenExpired(token) {
  if (!isJwtExpired(token)) return false;
  return isJwtExpired(localStorage.getItem(REFRESH_TOKEN_KEY));
}

// Supprimer l'ancien cookie access_token (le token ne doit vivre que dans localStorage)
document.cookie = "access_token=; max-age=0; path=/";

// Instance sans intercepteurs : l'appel /refresh doit porter le refresh token,
// pas l'access token ajouté automatiquement aux autres requêtes.
const refreshClient = axios.create();
let refreshPromise = null;

// Un seul renouvellement à la fois, partagé par toutes les requêtes en échec
function refreshSession() {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    refreshPromise = (
      isJwtExpired(refreshToken)
        ? Promise.reject(Object.assign(new Error("Refresh token absent ou expiré"), { sessionOver: true }))
        : refreshClient.post(`${config.apiUrl}/refresh`, null, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          })
    )
      .then((response) => {
        const { access_token, refresh_token } = response.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
        return access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Session définitivement expirée : retour à la connexion, puis à la page courante
function redirectToLogin() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  const current = window.location.pathname + window.location.search;
  if (window.location.pathname !== "/") {
    window.location.assign(`/?redirect=${encodeURIComponent(current)}`);
  }
}

function isAuthEndpoint(url = "") {
  return /\/(login|refresh|logout)(\?|$)/.test(url);
}

// "Se déconnecter": this browser stops receiving the alerts of the account,
// and the session can no longer be renewed (POST /logout). Never longer than
// a few seconds, whatever the network.
export async function logout() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const deviceId = localStorage.getItem(PUSH_DEVICE_ID_KEY);
  const pending = [
    unsubscribeThisDevice(deviceId).catch(() => {}),
    refreshToken
      ? refreshClient
          .post(`${config.apiUrl}/logout`, null, {
            headers: { Authorization: `Bearer ${refreshToken}` },
            timeout: 5000,
          })
          .catch(() => {})
      : Promise.resolve(),
  ];
  await Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, 5000)),
  ]);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(PUSH_DEVICE_ID_KEY);
}

// Intercepter les requêtes pour ajouter le token
axios.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (token) {
      requestConfig.headers["Authorization"] = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepter les réponses pour renouveler la session sur un 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Erreur réseau (pas de réponse) ou requête d'authentification : rien à renouveler
    if (
      !error.response ||
      error.response.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      const accessToken = await refreshSession();
      // Réessayer la requête avec le nouveau token
      originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      // Only a session really over leads to the login page: a network cut or
      // the API restarting (502) leaves the tokens, the next request retries
      const status = refreshError.response?.status;
      if (refreshError.sessionOver || status === 401 || status === 422)
        redirectToLogin();
      return Promise.reject(error);
    }
  }
);
